/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import React from "react";
import { effects } from "../../../components/effects";
import { colors } from "../../../components/colors";
import { StyledText } from "../../../assets/typography/typography";
import { MenuProduct } from "../models/Menus";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { formatMoneyAmount } from "../../../util";
import { UseFieldArrayRemove, useFormContext } from "react-hook-form";
import { Tooltip } from "antd";
import { Link, useParams } from "react-router-dom";
import { routes } from "../../../appRoutingHelpers";
import { MenuProductsFormValues } from "../MenuUpsert";

export const defaultImage =
  "https://mcusercontent.com/7963df4a41e0ed49b162683a8/images/abc71935-a66b-9720-d34a-402c85aba262.png";

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  product: MenuProduct;
  remove: UseFieldArrayRemove;
}

const ItemStyle = css`
  position: relative;
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  padding: 8px;
  width: 100%;
  div {
    div {
      .imageOverlay {
        display: none;
      }
      .panel {
        display: none;
      }
    }
  }
  border: none;
  background: ${colors.white};
  box-shadow: ${effects.buttonPrimary};
  gap: 16px;
  cursor: grab;
  &:hover {
    background: ${colors.whiteHover};
    div {
      div {
        .imageOverlay {
          display: flex;
        }
        .panel {
          display: flex;
        }
      }
    }
  }
`;

const topContainerStyle = css`
  display: flex;
  width: 100%;
  height: 100%;
  text-align: start;
  flex-direction: column;
  gap: 8px;
`;

const imageStyle = css`
  width: 100%;
  height: 120px;
  object-fit: cover;
  border-radius: 8px;
`;

const imageOverlayStyle = css`
  position: absolute;
  top: 0px;
  right: 0px;
  left: 0px;
  height: 120px;
  background-color: rgba(0, 0, 0, 0.25);
  z-index: 100;
  border-radius: 8px;
`;

const removeButtonStyle = css`
  display: flex;
  border: none;
  width: 24px;
  height: 24px;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  background-color: ${colors.white};
  box-shadow: ${effects.buttonPrimary};
  border-radius: 9999px;
  &:hover {
    background-color: ${colors.whiteHover};
  }
`;

const sortKeyStyle = css`
  display: flex;
  border: none;
  width: 24px;
  height: 24px;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.45);
  border-radius: 9999px;
`;

const panelStyle = css`
  position: absolute;
  display: flex;
  flex-direction: column;
  top: 8px;
  right: 8px;
  z-index: 110;
  gap: 8px;
`;

const imageWrapperStyle = css`
  position: relative;
`;

const grabHandleWrapperStyle = css`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;

const sortKeyWrapperStyle = css`
  position: absolute;
  left: 8px;
  top: 8px;
  z-index: 120;
`;

const tooltipStyle = {
  fontFamily: "Inter",
  fontSize: "14px",
};

export const MenuProductItem = ({ product, remove, ...rest }: Props) => {
  const { organization: organizationKeyName } = useParams<{
    organization: string;
  }>();
  const { watch } = useFormContext<MenuProductsFormValues>();

  const actualProduct = watch(`products.${product.fieldIndex!}`);

  return (
    <div css={ItemStyle} {...rest}>
      <div css={topContainerStyle}>
        <div css={grabHandleWrapperStyle}>
          <StyledText
            className="bodySubtextRegular mb-0"
            color={colors.textPrimary}
          >
            <FontAwesomeIcon icon={solid("grip")} />
          </StyledText>
        </div>
        <div css={imageWrapperStyle}>
          {product.imageUrl ? (
            <img src={product.imageUrl} css={imageStyle} alt="product" />
          ) : (
            <img src={defaultImage} css={imageStyle} alt="defaultproduct" />
          )}
          <div css={sortKeyWrapperStyle}>
            <Tooltip
              title="Sort position"
              arrow={false}
              placement="right"
              overlayInnerStyle={tooltipStyle}
            >
              <div css={sortKeyStyle}>
                <StyledText
                  className="bodySubtextSemibold mb-0"
                  color={colors.white}
                >
                  {actualProduct.sortKey}
                </StyledText>
              </div>
            </Tooltip>
          </div>
          <div className="panel" css={panelStyle}>
            <Tooltip
              title="Remove product"
              arrow={false}
              placement="left"
              overlayInnerStyle={tooltipStyle}
            >
              <button
                css={removeButtonStyle}
                onClick={() => {
                  remove(product.fieldIndex);
                }}
              >
                <StyledText
                  className="bodySubtextRegular mb-0"
                  color={colors.textPrimary}
                >
                  <FontAwesomeIcon icon={solid("trash-can")} />
                </StyledText>
              </button>
            </Tooltip>
            <Tooltip
              title="Edit product"
              arrow={false}
              placement="left"
              overlayInnerStyle={tooltipStyle}
            >
              <Link
                css={removeButtonStyle}
                to={routes.productEdit(organizationKeyName, actualProduct.id)}
              >
                <StyledText
                  className="bodySubtextRegular mb-0"
                  color={colors.textPrimary}
                >
                  <FontAwesomeIcon icon={solid("arrow-up-right-from-square")} />
                </StyledText>
              </Link>
            </Tooltip>
          </div>
          <div className="imageOverlay" css={imageOverlayStyle} />
        </div>
        <StyledText
          color={colors.textPrimary}
          className="bodyTextSemibold mb-0"
        >
          {product.name}
        </StyledText>
      </div>
      <StyledText color={colors.textPrimary} className="bodyTextRegular mb-0">
        {formatMoneyAmount(product.price.amount, product.price.currency)}
      </StyledText>
    </div>
  );
};
